import { useEffect, useState } from "react";

import classNames from "classnames/bind";
import RichText from "common/blocks/RichText";
import UIIcon from "common/blocks/UIIcon";
import Feedback from "features/faq/components/Feedback";
import { motion } from "framer-motion";
import type { ResolvedRichText } from "resolvers/data/richTextResolver";

import styles from "./QA.module.scss";

const cx = classNames.bind(styles);

export interface QaProps {
    question: string;
    answer: ResolvedRichText;
    id?: string;
    className?: string;
    isAlternateTheme?: boolean;
    isStaticPage?: boolean;
    clearBackground?: boolean;
    anchor?: string;
    openByDefault: boolean;
    handleCustomOpen?: any;
    openAll?: boolean;
    animationEnabled?: boolean;
    isTopicSection?: boolean;
    isSubSection?: boolean;
    index?: string;
    section?: string;
    isTopFaqSection?: boolean;
    onOpenFaqItem?: (itemName: string) => void;
    onCloseFaqItem?: (itemName: string) => void;
}

const QA = ({
    id,
    question,
    answer,
    isAlternateTheme,
    isStaticPage,
    clearBackground,
    openByDefault,
    handleCustomOpen,
    anchor,
    className,
    openAll,
    animationEnabled = false,
    isTopicSection,
    isSubSection,
    section,
    index,
    isTopFaqSection,
    onOpenFaqItem,
    onCloseFaqItem,
}: QaProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(openByDefault);
    const isTopicOrSubSection = isTopicSection || isSubSection;

    useEffect(() => {
        if (isOpen) onOpenFaqItem?.(question);
        if (!isOpen) onCloseFaqItem?.(question);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (
            handleCustomOpen &&
            handleCustomOpen.current &&
            anchor &&
            !handleCustomOpen.current[anchor]
        ) {
            handleCustomOpen.current[anchor] = () => {
                setIsOpen(true);
                setTimeout(() => {
                    document.getElementById(anchor)?.scrollIntoView();
                }, 1); // hack: scroll after opening.
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const target = document.querySelector(":target");

        if (target && target.id === anchor) {
            setIsOpen(true);
            setTimeout(() => target.scrollIntoView(), 500); // hack: scroll after opening.
        }
    }, [anchor]);

    const dataLayerPush = (event: { event: string; question: string; section?: string }) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(event);
    };

    return (
        <div
            id={anchor}
            className={cx("block", className, {
                isOpen,
                isTopicSection,
                isSubSection,
                isAlternateTheme,
                isStaticPage,
                clearBackground,
            })}
        >
            <motion.button
                initial={false}
                onClick={() => {
                    setIsOpen((isOpen) => !isOpen);
                    !isOpen &&
                        isTopicOrSubSection &&
                        dataLayerPush({
                            event: "faq_open",
                            question: question,
                            section: section ? section : "TopFaqs",
                        });
                }}
                className={cx("header", { isOpen })}
                aria-controls={question}
                aria-expanded={isOpen}
            >
                <a
                    className={cx("anchor")}
                    href={`#${anchor}`}
                    onClick={(e) => {
                        e.preventDefault();
                    }}
                >
                    <div className={cx("titleWrapper")}>
                        {isTopicOrSubSection && index && (
                            <h3 className={cx("title", "index")}>{index}.</h3>
                        )}
                        <h3 className={cx("title", { isTopicSection, isSubSection })}>
                            {question}
                        </h3>
                    </div>
                </a>
                {!openAll && (
                    <UIIcon
                        icon="ChevronDown"
                        className={cx("icon", { isOpen, animationEnabled })}
                    />
                )}
            </motion.button>

            <motion.section
                key="children"
                initial="collapsed"
                variants={{
                    open: { height: "auto", display: "block" },
                    collapsed: { height: 0, display: "none" },
                }}
                transition={{
                    duration: animationEnabled ? 0.3 : 0,
                    ...(animationEnabled && { ease: [0.04, 0.62, 0.23, 0.98] }),
                }}
                animate={openAll === true || isOpen ? "open" : "collapsed"}
                className={cx("content")}
                aria-hidden={!isOpen}
            >
                <motion.div
                    initial="collapsed"
                    variants={{
                        collapsed: {
                            opacity: 0,
                            ...(animationEnabled && { y: [0, 10] }),
                            transition: {
                                duration: animationEnabled ? 0.3 : 0,
                                delay: 0,
                                ...(animationEnabled && { ease: [0.04, 0.62, 0.23, 0.98] }),
                            },
                        },
                        open: {
                            opacity: 1,
                            transition: {
                                duration: animationEnabled ? 0.3 : 0,
                                delay: animationEnabled ? 0.2 : 0,
                            },
                        },
                    }}
                    animate={openAll === true || isOpen ? "open" : "collapsed"}
                    className={cx("contentWrap")}
                    aria-hidden={!isOpen}
                >
                    <RichText
                        {...answer}
                        className={cx("answer")}
                        props={{ imageType: "article" }}
                    />
                    {isTopicOrSubSection && (
                        <Feedback
                            components={{
                                heading: "I found this useful",
                                questionId: id ?? question,
                                question: question,
                                section: section,
                            }}
                        />
                    )}
                </motion.div>
            </motion.section>
        </div>
    );
};

export default QA;
